import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { fetchAuthSession } from "aws-amplify/auth";
import "./styles.css";
import "./shared/coralreef/scroll.css";
import { App } from "./App";
import { bootstrapShellHost } from "./shell-handle/bootstrap";
import { createAuthApi } from "./shell-handle/auth";
import {
  registerMfes,
  startSingleSpa,
  attachMfeErrorHandler,
} from "./single-spa-config/apps";

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root element");

// Mirror shell path → window.location.hash for compatibility with
// downstream tooling (marlin-v2 e2e tests, desktop-app screen-state
// persistence) that historically relied on tide's hash routing. The
// shell uses tanstack-router with browser history for its real
// routing; this listener just keeps the hash in sync as a side
// channel so `window.location.hash` reads `#/welcome` after the
// shell navigates to `/`.
function pathToHash(pathname: string, search: string): string {
  const target = pathname === "/" ? "/welcome" : pathname;
  return "#" + target + (search || "");
}
function syncHashToPath() {
  const desired = pathToHash(window.location.pathname, window.location.search);
  if (window.location.hash !== desired) {
    history.replaceState(
      history.state,
      "",
      window.location.pathname + window.location.search + desired,
    );
  }
}
syncHashToPath();
window.addEventListener("popstate", syncHashToPath);
const _origPushState = history.pushState.bind(history);
history.pushState = function (...args: Parameters<typeof history.pushState>) {
  _origPushState(...args);
  syncHashToPath();
};
const _origReplaceState = history.replaceState.bind(history);
history.replaceState = function (
  ...args: Parameters<typeof history.replaceState>
) {
  _origReplaceState(...args);
  syncHashToPath();
};

const { host, client } = bootstrapShellHost();

const TIDE_TOKEN_KEY = "id_token";
const AKVA_TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refresh_token";

function bridgeTokenToMfes(token: string) {
  try {
    localStorage.setItem(TIDE_TOKEN_KEY, token);
    localStorage.setItem(AKVA_TOKEN_KEY, token);
  } catch {
    /* storage may be disabled */
  }
}

async function bridgeRefreshTokenToMfes() {
  try {
    const session = await fetchAuthSession();
    const access = session.tokens?.accessToken?.toString();
    const id = session.tokens?.idToken?.toString();
    const value = access || id;
    if (value) localStorage.setItem(REFRESH_TOKEN_KEY, value);
  } catch {
    /* not signed in */
  }
}

function clearMfeTokens() {
  try {
    localStorage.removeItem(TIDE_TOKEN_KEY);
    localStorage.removeItem(AKVA_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

const auth = createAuthApi();
auth.onTokenRefresh((t) => {
  bridgeTokenToMfes(t);
  void bridgeRefreshTokenToMfes();
});
auth.onSessionExpired(() => {
  clearMfeTokens();
  if (
    window.location.pathname !== "/login" &&
    !window.location.pathname.startsWith("/login/") &&
    !window.location.pathname.startsWith("/submerged")
  ) {
    window.location.assign("/login");
  }
});
auth.startTokenWatcher();
{
  const sp = new URLSearchParams(window.location.search);
  const start = sp.get("start");
  const end = sp.get("end");
  if (start || end) host.handle.dateRange.setRange(start, end);
  const sitesRaw = sp.get("sites");
  if (sitesRaw) {
    try {
      const parsed = JSON.parse(sitesRaw);
      if (Array.isArray(parsed))
        host.handle.sites.setSelectedSiteIds(
          parsed.map(Number).filter((n) => Number.isFinite(n)),
        );
    } catch {}
  }
  const cagesRaw = sp.get("cages");
  if (cagesRaw) {
    try {
      const parsed = JSON.parse(cagesRaw);
      if (Array.isArray(parsed))
        host.handle.sites.setSelectedCageIds(parsed.map(String));
    } catch {}
  }
}
attachMfeErrorHandler(host.mfeError);
registerMfes(host.handle);

createRoot(root).render(
  <StrictMode>
    <App host={host} client={client} />
  </StrictMode>,
);

function isLoginRoute(): boolean {
  const p = window.location.pathname;
  return p === "/login" || p.startsWith("/login/");
}

function isAuthExempt(): boolean {
  return isLoginRoute() || window.location.pathname.startsWith("/submerged");
}

function isAuthError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name ?? "";
  const msg = (err as { message?: string } | null)?.message ?? "";
  if (name === "NotAuthorizedException") return true;
  if (/refresh\s*token/i.test(msg)) return true;
  if (/not\s*authorized/i.test(msg)) return true;
  return false;
}

async function bootstrapSession(): Promise<boolean> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const t = session.tokens?.idToken?.toString();
    if (!t) return false;
    bridgeTokenToMfes(t);
    await bridgeRefreshTokenToMfes();
    await host.loadInitialData();
    return true;
  } catch (err) {
    console.warn("[shell] bootstrap session failed:", err);
    if (isAuthError(err)) {
      clearMfeTokens();
      try {
        await auth.signOut();
      } catch {
        /* ignore */
      }
      if (!isAuthExempt()) {
        window.location.assign("/login");
      }
    }
    return false;
  }
}

void (async () => {
  if (isAuthExempt()) {
    startSingleSpa();
    return;
  }
  const ok = await bootstrapSession();
  if (ok || isAuthExempt()) {
    startSingleSpa();
  }
  // else: redirect already issued; do not start single-spa to avoid
  // tide MFE booting against a dead session.
})();
